import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Smile, Meh, Frown, X } from "lucide-react";
import { recordFeedback } from "@/lib/neuroStore";

// Quick 3-tap post-session feedback: mood, perceived difficulty, energy.
// Non-diagnostic self-report only.

const SCALE = [1, 2, 3, 4, 5];

const Row = ({ label, hint, value, onChange, testKey }) => (
  <div className="rounded-2xl border border-white/10 bg-black/30 px-4 py-3">
    <div className="flex items-baseline justify-between">
      <div className="text-xs uppercase tracking-widest text-zinc-300 font-semibold">
        {label}
      </div>
      <div className="text-[10px] text-zinc-500">{hint}</div>
    </div>
    <div className="mt-2 flex gap-1.5">
      {SCALE.map((n) => (
        <button
          key={n}
          data-testid={`feedback-${testKey}-${n}`}
          onClick={() => onChange(n)}
          className={`flex-1 py-2 rounded-lg border text-sm font-semibold ${
            value === n
              ? "border-cyan-400 bg-cyan-400/20 text-white"
              : "border-white/10 bg-white/5 hover:bg-white/10 text-zinc-300"
          }`}
        >
          {n}
        </button>
      ))}
    </div>
  </div>
);

export default function SessionFeedbackModal({
  open,
  category,
  level,
  onClose,
}) {
  const [mood, setMood] = useState(0);
  const [difficulty, setDifficulty] = useState(0);
  const [energy, setEnergy] = useState(0);

  const submit = () => {
    recordFeedback(category, level, { mood, difficulty, energy });
    onClose?.();
    setMood(0);
    setDifficulty(0);
    setEnergy(0);
  };

  const skip = () => {
    onClose?.();
    setMood(0);
    setDifficulty(0);
    setEnergy(0);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={skip}
          data-testid="feedback-modal"
        >
          <motion.div
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0A0A14] p-6 sm:p-7"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-violet-300 font-semibold">
                  Quick check-in
                </div>
                <h3 className="mt-1 font-display text-xl font-semibold">
                  How was that session?
                </h3>
              </div>
              <button
                data-testid="feedback-close"
                onClick={skip}
                className="w-8 h-8 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="mt-2 text-xs text-zinc-400">
              Optional self-report — helps you notice patterns over time. Not
              a clinical measure.
            </p>

            <div className="mt-4 space-y-2.5">
              <Row
                label="Mood"
                hint="1 low · 5 great"
                value={mood}
                onChange={setMood}
                testKey="mood"
              />
              <Row
                label="Perceived difficulty"
                hint="1 easy · 5 tough"
                value={difficulty}
                onChange={setDifficulty}
                testKey="difficulty"
              />
              <Row
                label="Energy"
                hint="1 drained · 5 fresh"
                value={energy}
                onChange={setEnergy}
                testKey="energy"
              />
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                data-testid="feedback-skip"
                onClick={skip}
                className="text-xs text-zinc-400 hover:text-white px-3 py-2"
              >
                Skip
              </button>
              <button
                data-testid="feedback-submit"
                onClick={submit}
                disabled={!mood || !difficulty || !energy}
                className="inline-flex items-center gap-1.5 rounded-full bg-violet-400 text-black font-medium px-5 py-2 text-sm hover:bg-violet-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Smile className="w-3.5 h-3.5" />
                Save
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
